import {Injectable} from '@angular/core';
import {flatMap, intersectionBy, isEmpty, isString, isUndefined, omit} from 'lodash-es';

@Injectable()
export class UtilsService {

  public escapeRegExp(str: string) {
    return str.replace(/[.*+?^${}()|[\]\\]/gi, '\\$&'); // $& means the whole matched string
  }

  /**
   * Tells whether supplied {@param object} is empty or not.
   * @param object
   * @param {string[]} omitPaths, keys which should be ignored while checking emptiness.
   * @returns {boolean}
   */
  public isEmpty(object, omitPaths: string[] = []): boolean {
    return isEmpty(omit(object, omitPaths));
  }

  public parseTemplateString(templateString: string, replacements: Object,
                             enclosingLeft: string = '{', enclosingRight: string = '}') {
    const regexStr = `(\\$)?\\${enclosingLeft}(.*?)\\${enclosingRight}`;
    const gRegex = new RegExp(regexStr, 'g');
    // First find all the instances where replacement is required
    return templateString.replace(gRegex, (matchedStr: string) => {
      // Now get the key for replacement
      const rgx = new RegExp(regexStr);
      const matchedKey = matchedStr.match(rgx).pop();
      // Now return the replacement if it exists else return the match
      return replacements[matchedKey];
    });
  }

  /**
   * Polyfil for Object.entries
   * @param object
   */
  public objectEntries(obj): any[] {
    if (Object['entries']) {
      return Object['entries'](obj);
    }
    const ent = [];
    for (const x in obj) {
      if (obj.hasOwnProperty(x)) {
        ent.push([x, obj[x]]);
      }
    }
    return ent;
  }

  public getBaseLog(base: number, value: number) {
    return Math.log(value) / Math.log(base);
  }

  /**
   * Returns true if array contains empty values.
   * @param {any[]} array
   * @returns {boolean}
   */
  public hasEmpty(array: any[]): boolean {
    let length = array.length;
    while (length !== 0) {
      if (isUndefined(array[--length])) {
        return true;
      }
    }
    return false;
  }

  // Taken from here
  // https://stackoverflow.com/questions/18749591/encode-html-entities-in-javascript
  // Converts all special chars to HTML special chars
  public converToHTMLEntities(str) {
    if (isString(str)) {
      return str.replace(/[\u00A0-\u9999<>\&]/gim, (i) => {
        return '&#' + i.charCodeAt(0) + ';';
      });
    }
    return str;
  }

  /**
   * This function takes text and if it finds links, it
   * wraps them in a tags
   * @param msg
   * @returns {string}
   */
  public addATagToLinks(msg) {
    // Found the url regex here but was modified
    // https://stackoverflow.com/questions/1500260/detect-urls-in-text-with-javascript

    /*tslint:disable:max-line-length*/
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*\.[-A-Z0-9+&@#\/%=~_|\?]+)(?![^<>]*>|[^"]*?<\/a)/ig;

    return msg.replace(urlRegex, '<a href="$1" target="_blank" class="underlined-link">$1</a>');
  }

  /**
   * flatten list by key
   * @param list: Array
   * @param key:string
   * @returns the list of keyed values
   */
  public flattenBy(list, key) {
    const flatten = (collection, flatBy) => flatMap(collection, (object) => {
      if (object[flatBy] instanceof Array) {
        return object[flatBy];
      }
      return [object[flatBy]];
    });

    return flatten(list, key);
  }

  /**
   * This function is to be used since we cannot use the spread operator
   * in typescript functions without optional arguments
   * @param arr - An array of arrays from which this needs to happen
   * @param iteratee - same as the iteratee in intersectionBy
   * @returns {any[]}
   *
   * TODO: Use spread operator here once this functionality is available
   * Follow this issue https://github.com/Microsoft/TypeScript/issues/4130
   */
  public intersectionByArr(arr: Array<any[]>, iteratee: any) {
    // Get the first element as a start
    const retArr = arr.pop();
    return arr.reduce((f, v) => {
      // Get intersection of current result with
      // the next value
      return intersectionBy(f, v, iteratee);
    }, retArr);
  }

  public parseUrlString(params) {
    return JSON.parse('{"' + params.replace(/&/g, '","').replace(/=/g, '":"') + '"}', function (key, value) {
      return key === '' ? value : decodeURIComponent(value.replace(/\+/g, ' '));
    });
  }

  public getQueryString(field, url) {
    if (!url) {
      return;
    }
    const reg = new RegExp(`[?&]${field}=([^&#]*)`, 'i');
    const str = reg.exec(url);
    return str ? str[1] : null;
  }

  public getQueryParams(params) {
    const urlParams = [];
    params.split('&').forEach(function (qparam, i) {
      const param = qparam.split('=');
      urlParams[param[0]] = param[1];
    });
    return urlParams;
  }
}
